import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import { ArrowRight, ArrowLeft, Home, Heart, Baby, Users, KeyRound, CalendarClock } from "lucide-react";

/**
 * VAULT Life Moments — situation-first storytelling pages.
 *
 * Each moment follows the same shape: The Situation → Why it becomes
 * hard → What organisation looks like → How VAULT helps → Related
 * reading → Create Your VAULT. No advice, no advocacy — organisation
 * only.
 */
const MOMENTS = {
    "moving-home": {
        icon: Home, tone: "teal",
        title: "Moving Home",
        deck: "Moving is the moment a household discovers where its financial paperwork has been quietly hiding.",
        situation: "A move is really three moves: the physical one, the administrative one, and the informational one. The first is planned. The second is chaotic. The third is invisible until something goes wrong three months later.",
        hard: "New utilities to set up. Old ones to close. Insurance to transfer. Documents packed in a box marked 'important'. A dozen small addresses that need updating in a dozen small places.",
        organised: [
            "One list of every utility, subscription, and account tied to the old address",
            "A single place to store the new lease or property paper the day it exists",
            "A shared calendar for the switchovers and the first three months of new bills",
            "A visible list of everyone who still has the wrong address on file",
        ],
        help: "VAULT gives the move an information layer that survives the move.",
        articles: ["family-vault-checklist", "information-problem"],
    },
    "getting-married": {
        icon: Heart, tone: "violet",
        title: "Getting Married",
        deck: "The organisational conversations most couples never have — and the tiny amount of design that saves years of quiet friction.",
        situation: "Two financial lives are about to share more surfaces than either of them has ever shared before. Nobody teaches this. Every couple designs it themselves, usually by accident.",
        hard: "Insurance to combine or keep separate. Joint accounts. Nomination updates. Household subscriptions. Whose loan is whose. What stays personal. What becomes together.",
        organised: [
            "One short list agreed as clearly Joint — the home, the household bills, the shared insurance",
            "A comfortable understanding of what belongs in Together — visible to both, still individually owned",
            "Personal financial identity respected — Mine and Yours never merged by default",
            "A continuity document in case one partner has to help the other tomorrow",
        ],
        help: "VAULT Together is designed for exactly this — shared where it matters, private where it counts.",
        articles: ["couples-money-privacy", "family-vault-checklist"],
    },
    "starting-a-family": {
        icon: Baby, tone: "coral",
        title: "Starting a Family",
        deck: "A new family member creates a small mountain of records that will matter for the next twenty years.",
        situation: "In the first year, a household generates more permanent records than in the previous ten combined. Hospital paperwork. Government IDs. Nominations. School and vaccination files. Insurance updates that must not be missed.",
        hard: "None of it is complicated. All of it is easy to misfile. The problem shows up not on the day the paperwork is created — but on the day someone has to find it.",
        organised: [
            "A separate space for each child's identity documents from day one",
            "Vaccination and medical records kept alongside — not scattered across three apps",
            "Insurance nominations updated the same week the child arrives",
            "A visible list of upcoming administrative dates for the next twelve months",
        ],
        help: "VAULT Documents plus a family calendar removes twenty small worries in one weekend.",
        articles: ["family-vault-checklist", "information-problem"],
    },
    "managing-parents": {
        icon: Users, tone: "amber",
        title: "Managing Parents' Important Records",
        deck: "The most valuable financial organisation project most adults ever do — usually postponed until it becomes urgent.",
        situation: "At some point, a child helps a parent with their finances. Sometimes gently over years. Sometimes suddenly, in an afternoon. Neither version is easy if nobody has quietly organised the records first.",
        hard: "Which insurer? Which bank? Which policy number? Which nominee? Where is the will? Which cupboard, which folder, which app? The information exists somewhere — but 'somewhere' is not a system.",
        organised: [
            "One list of every policy, account, and important record — with contacts and reference numbers",
            "Nominations verified and up to date — not assumed",
            "A continuity document that explains where everything lives",
            "Read access shared with the right family member — reversibly",
        ],
        help: "VAULT holds all of this in one place, with sharing designed for exactly this situation.",
        articles: ["family-vault-checklist", "couples-money-privacy"],
    },
    "buying-a-home": {
        icon: KeyRound, tone: "emerald",
        title: "Buying a Home",
        deck: "A home is bought over months and lived in for decades. The paperwork should survive both.",
        situation: "Buying a home involves more documents than any other financial event in most households' lives. Some are needed once. Some will be needed in ten years. Almost none will be memorable when they are.",
        hard: "Sale agreements. Loan documents. Insurance certificates. Society share papers. Tax certificates. Every quarter for the next ten years, one of these will need to be found in under an hour.",
        organised: [
            "Every home document filed on the day it arrives — not the day it's needed",
            "Loan schedule and EMI dates on one calendar with one owner",
            "Home insurance renewal linked to a reminder that actually gets seen",
            "Warranty documents for anything valuable, kept with their receipts",
        ],
        help: "VAULT gives a home its own filing cabinet — one that stays organised for the decades the home is lived in.",
        articles: ["family-vault-checklist", "bank-balance-vs-net-worth"],
    },
    "year-end": {
        icon: CalendarClock, tone: "gold",
        title: "Year-End Organisation",
        deck: "The one hour of financial organisation each year that makes the next twelve months noticeably calmer.",
        situation: "Year-end is when a household discovers what it did and didn't organise during the year. Tax season is the deadline. But the real reward is starting the new year with a financial life that finally fits on one page.",
        hard: "Statements to gather. Investment records to reconcile. Insurance premiums to note. Subscriptions to audit. Documents that arrived in email and were never filed. The receipts nobody wanted to think about in July.",
        organised: [
            "One place with every year-end statement — investment, bank, credit card, loan",
            "Every annual renewal accounted for, on a calendar the household actually opens",
            "A subscription audit — what to keep, what to downgrade, what to cancel",
            "A short document that captures net worth at year-end. Once a year, in ten minutes.",
        ],
        help: "VAULT turns the annual scramble into an hour of quiet review.",
        articles: ["subscriptions", "bank-balance-vs-net-worth", "information-problem"],
    },
};

const TONE_STOP = {
    gold: "linear-gradient(135deg,#E6D5A8 0%,#C6A86B 60%,#8f6f38 100%)",
    coral: "linear-gradient(135deg,#FED7AA 0%,#F97316 55%,#7C2D12 100%)",
    emerald: "linear-gradient(135deg,#A7F3D0 0%,#10B981 55%,#065F46 100%)",
    teal: "linear-gradient(135deg,#99F6E4 0%,#14B8A6 55%,#134E4A 100%)",
    violet: "linear-gradient(135deg,#DDD6FE 0%,#8B5CF6 55%,#4C1D95 100%)",
    amber: "linear-gradient(135deg,#FDE68A 0%,#F59E0B 55%,#78350F 100%)",
};

function MomentsIndex() {
    return (
        <PublicShell>
            <div className="lm-root" data-testid="life-moments-index">
                <style>{LM_CSS}</style>
                <section className="lm-container lm-hero">
                    <div className="lm-eyebrow">VAULT LIFE MOMENTS</div>
                    <h1 className="lm-title">The situations when a household actually needs to be organised.</h1>
                    <p className="lm-lede">
                        Six moments most families move through — and the small amount of quiet design that makes each one easier.
                    </p>
                </section>
                <section className="lm-container lm-grid" data-testid="life-moments-grid">
                    {Object.entries(MOMENTS).map(([slug, m]) => {
                        const Icon = m.icon;
                        return (
                            <Link key={slug} to={`/life-moments/${slug}`} className="lm-card" data-testid={`moment-${slug}`}>
                                <div className="lm-card__icon" style={{ background: TONE_STOP[m.tone] }}>
                                    <Icon size={20} strokeWidth={1.8} color="#0F1E3A"/>
                                </div>
                                <h3 className="lm-card__title">{m.title}</h3>
                                <p className="lm-card__teaser">{m.deck}</p>
                                <span className="lm-card__cta">Read <ArrowRight size={12}/></span>
                            </Link>
                        );
                    })}
                </section>
            </div>
        </PublicShell>
    );
}

function MomentPage({ slug, moment }) {
    const Icon = moment.icon;
    const related = useMemo(() =>
        Object.entries(MOMENTS).filter(([s]) => s !== slug).slice(0, 3),
        [slug]);

    return (
        <PublicShell>
            <div className="lm-root" data-testid={`life-moment-${slug}`}>
                <style>{LM_CSS}</style>

                <header className="lm-detail-hero">
                    <div className="lm-detail-hero__bg" style={{ background: TONE_STOP[moment.tone] }}/>
                    <div className="lm-container lm-detail-hero__inner">
                        <Link to="/life-moments" className="lm-back">
                            <ArrowLeft size={13}/> VAULT Life Moments
                        </Link>
                        <div className="lm-detail-hero__icon" style={{ background: TONE_STOP[moment.tone] }}>
                            <Icon size={22} strokeWidth={1.8} color="#0F1E3A"/>
                        </div>
                        <h1 className="lm-detail-hero__title">{moment.title}</h1>
                        <p className="lm-detail-hero__deck">{moment.deck}</p>
                    </div>
                </header>

                <article className="lm-container lm-body">
                    <section className="lm-sec">
                        <h2 className="lm-h2">The situation</h2>
                        <p className="lm-para">{moment.situation}</p>
                    </section>

                    <section className="lm-sec">
                        <h2 className="lm-h2">Why it becomes hard</h2>
                        <p className="lm-para">{moment.hard}</p>
                    </section>

                    <section className="lm-sec">
                        <h2 className="lm-h2">What good organisation looks like</h2>
                        <ul className="lm-checklist">
                            {moment.organised.map((line, i) => (
                                <li key={i}>{line}</li>
                            ))}
                        </ul>
                    </section>

                    <section className="lm-sec">
                        <h2 className="lm-h2">How VAULT helps</h2>
                        <p className="lm-para">{moment.help}</p>
                    </section>

                    <div className="lm-finale" data-testid="lm-finale">
                        <div className="lm-finale__eyebrow">CREATE YOUR VAULT</div>
                        <h3 className="lm-finale__title">Start with the parts that matter most.</h3>
                        <div className="lm-finale__row">
                            <Link to="/signup" className="lm-btn lm-btn--gold" data-testid="lm-signup">
                                Create Your VAULT <ArrowRight size={14}/>
                            </Link>
                            <Link to="/demo" className="lm-btn lm-btn--ghost" data-testid="lm-demo">
                                Explore Demo
                            </Link>
                        </div>
                    </div>

                    <section className="lm-related">
                        <div className="lm-related__eyebrow">RELATED READING</div>
                        <div className="lm-related__grid">
                            {moment.articles.map((s) => (
                                <Link key={s} to={`/learn/articles/${s}`} className="lm-related__card">
                                    <div className="lm-related__cat">JOURNAL</div>
                                    <div className="lm-related__title">{s.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}</div>
                                </Link>
                            ))}
                            {related.slice(0, 3 - moment.articles.length).map(([s, m]) => (
                                <Link key={s} to={`/life-moments/${s}`} className="lm-related__card">
                                    <div className="lm-related__cat">LIFE MOMENT</div>
                                    <div className="lm-related__title">{m.title}</div>
                                </Link>
                            ))}
                        </div>
                    </section>
                </article>
            </div>
        </PublicShell>
    );
}

export default function LifeMoments() {
    const { slug } = useParams();
    if (!slug) return <MomentsIndex/>;
    const m = MOMENTS[slug];
    if (!m) return <Navigate to="/life-moments" replace/>;
    return <MomentPage slug={slug} moment={m}/>;
}

const LM_CSS = `
.lm-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.lm-container { max-width: 1080px; margin: 0 auto; padding: 0 28px; }
.lm-hero { padding: 72px 28px 40px; max-width: 780px; }
.lm-eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.lm-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(36px, 5.4vw, 58px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.04; margin: 0 0 18px; }
.lm-lede { font-size: 17px; line-height: 1.6; color: #57534E; max-width: 620px; margin: 0; }
.lm-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; padding: 20px 28px 0; }
@media (max-width: 900px) { .lm-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .lm-grid { grid-template-columns: 1fr; } }
.lm-card { display: flex; flex-direction: column; padding: 26px 24px 22px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 20px; text-decoration: none; color: inherit; transition: transform 220ms cubic-bezier(0.16,1,0.3,1), box-shadow 220ms cubic-bezier(0.16,1,0.3,1), border-color 220ms cubic-bezier(0.16,1,0.3,1); }
.lm-card:hover { transform: translateY(-4px); box-shadow: 0 24px 48px -20px rgba(15,30,58,0.2); border-color: rgba(198,168,107,0.35); }
.lm-card__icon { width: 44px; height: 44px; border-radius: 14px; display: grid; place-items: center; margin-bottom: 16px; box-shadow: 0 8px 20px -8px rgba(15,30,58,0.2); }
.lm-card__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.01em; color: #0F1E3A; margin: 0 0 8px; }
.lm-card__teaser { font-size: 13.5px; line-height: 1.5; color: #57534E; margin: 0 0 14px; flex: 1; }
.lm-card__cta { display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; }
.lm-detail-hero { position: relative; padding: 72px 28px 56px; overflow: hidden; }
.lm-detail-hero__bg { position: absolute; inset: 0; opacity: 0.16; }
.lm-detail-hero__inner { position: relative; max-width: 780px; }
.lm-back { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #57534E; text-decoration: none; margin-bottom: 20px; }
.lm-back:hover { color: #0F1E3A; }
.lm-detail-hero__icon { width: 48px; height: 48px; border-radius: 14px; display: grid; place-items: center; margin-bottom: 20px; box-shadow: 0 12px 24px -8px rgba(15,30,58,0.24); }
.lm-detail-hero__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(34px, 5.4vw, 58px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.05; color: #0F1E3A; margin: 0 0 16px; }
.lm-detail-hero__deck { font-size: 18px; line-height: 1.55; color: #44403C; margin: 0; max-width: 620px; }
.lm-body { max-width: 720px; margin: 0 auto; padding: 32px 28px 0; }
.lm-sec { margin-bottom: 32px; }
.lm-h2 { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; font-weight: 500; letter-spacing: -0.012em; color: #0F1E3A; margin: 0 0 12px; }
.lm-para { font-size: 16.5px; line-height: 1.65; color: #292524; margin: 0; }
.lm-checklist { list-style: none; padding: 20px 24px; margin: 0; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 16px; display: flex; flex-direction: column; gap: 10px; }
.lm-checklist li { position: relative; padding-left: 20px; font-size: 15px; line-height: 1.55; color: #292524; }
.lm-checklist li::before { content: ""; position: absolute; left: 0; top: 8px; width: 8px; height: 8px; border-radius: 999px; background: #C6A86B; }
.lm-finale { margin: 40px 0; padding: 32px 32px; background: linear-gradient(140deg,#0F1E3A 0%,#0A1424 100%); color: #F1F5F9; border-radius: 24px; text-align: center; }
.lm-finale__eyebrow { font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #E6D5A8; margin-bottom: 8px; }
.lm-finale__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; font-weight: 500; color: #F1F5F9; margin: 0 0 18px; }
.lm-finale__row { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
.lm-btn { display: inline-flex; align-items: center; gap: 6px; padding: 12px 22px; border-radius: 999px; font-size: 13.5px; font-weight: 600; text-decoration: none; transition: transform 180ms cubic-bezier(0.16,1,0.3,1); }
.lm-btn--gold { background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); }
.lm-btn--gold:hover { transform: translateY(-1px); }
.lm-btn--ghost { background: transparent; color: #F1F5F9; border: 1px solid rgba(241,245,249,0.24); }
.lm-btn--ghost:hover { background: rgba(241,245,249,0.06); }
.lm-related__eyebrow { font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #78716C; margin-bottom: 14px; }
.lm-related__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
@media (max-width: 720px) { .lm-related__grid { grid-template-columns: 1fr; } }
.lm-related__card { padding: 18px 20px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 16px; text-decoration: none; transition: transform 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms cubic-bezier(0.16,1,0.3,1); }
.lm-related__card:hover { transform: translateY(-2px); border-color: rgba(198,168,107,0.32); }
.lm-related__cat { font-size: 9.5px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; margin-bottom: 6px; }
.lm-related__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 17px; font-weight: 500; color: #0F1E3A; line-height: 1.22; }
`;
